(() => {
  'use strict';

  if (window.NoyafaBrandPage) {
    window.NoyafaBrandPage.initAll();
    return;
  }

  const controllers = new Map();
  // Motion is managed by viewport and visibility controls instead of OS preferences.
  const reducedMotion = { matches: false };
  let gsapPromise;
  let sharedEventsBound = false;
  let videoReturnFocus = null;
  let inquiryReturnFocus = null;
  let savedRootOverflow = '';
  let chromeSyncFrame = 0;
  let chromeResizeObserver = null;
  let promoObserver = null;
  let assetWarmupScheduled = false;
  const assetWarmupTimers = [];
  let lastViewportWidth = window.innerWidth;
  const inquiryDraftKey = 'noyafa-brand-inquiry-draft-v1';
  const inquiryDraftLifetime = 24 * 60 * 60 * 1000;
  const clamp = (value, min = 0, max = 1) => Math.min(max, Math.max(min, value));

  function loadScript(src, key, ready) {
    if (ready()) return Promise.resolve();
    const existing = document.querySelector(`script[data-noyafa-loader="${key}"]`);
    if (existing) {
      return new Promise((resolve, reject) => {
        existing.addEventListener('load', resolve, { once: true });
        existing.addEventListener('error', reject, { once: true });
      });
    }

    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = src;
      script.async = true;
      script.dataset.noyafaLoader = key;
      script.addEventListener('load', resolve, { once: true });
      script.addEventListener('error', reject, { once: true });
      document.head.appendChild(script);
    });
  }

  function ensureGsap() {
    if (window.gsap && window.ScrollTrigger) {
      window.gsap.registerPlugin(window.ScrollTrigger);
      return Promise.resolve(true);
    }
    if (gsapPromise) return gsapPromise;

    gsapPromise = loadScript(
      'https://cdnjs.cloudflare.com/ajax/libs/gsap/3.12.2/gsap.min.js',
      'gsap',
      () => Boolean(window.gsap)
    )
      .then(() => loadScript(
        'https://cdnjs.cloudflare.com/ajax/libs/gsap/3.12.2/ScrollTrigger.min.js',
        'scroll-trigger',
        () => Boolean(window.ScrollTrigger)
      ))
      .then(() => {
        window.gsap.registerPlugin(window.ScrollTrigger);
        return true;
      })
      .catch(() => false);

    return gsapPromise;
  }

  function syncStoreChrome() {
    const header = document.getElementById('HeaderWrapper');
    const height = header ? Math.max(0, Math.round(header.getBoundingClientRect().height)) : 0;
    document.documentElement.style.setProperty('--noyafa-brand-sticky-offset', `${height}px`);

    if (header && !chromeResizeObserver && 'ResizeObserver' in window) {
      chromeResizeObserver = new ResizeObserver(scheduleStoreChromeSync);
      chromeResizeObserver.observe(header);
    }
  }

  function scheduleStoreChromeSync() {
    if (chromeSyncFrame) cancelAnimationFrame(chromeSyncFrame);
    chromeSyncFrame = requestAnimationFrame(() => {
      chromeSyncFrame = 0;
      syncStoreChrome();
      const viewportWidth = window.innerWidth;
      if (Math.abs(viewportWidth - lastViewportWidth) > 2) {
        lastViewportWidth = viewportWidth;
        window.ScrollTrigger?.refresh();
      }
    });
  }

  function dismissStorePromo() {
    if (!document.body.classList.contains('noyafa-brand-page-active')) return;
    document.querySelectorAll('.react-responsive-modal-root').forEach((root) => {
      if (!root.querySelector('#trustoo-popup-optIn-title')) return;
      root.hidden = true;
      root.querySelector('.ecomsend__Modal__CloseButton')?.dispatchEvent(
        new MouseEvent('click', { bubbles: true })
      );
    });
  }

  function syncPageState() {
    const active = Boolean(document.querySelector('[data-noyafa-brand-section]'));
    document.documentElement.classList.toggle('noyafa-brand-page-active', active);
    document.body.classList.toggle('noyafa-brand-page-active', active);
    syncStoreChrome();
    dismissStorePromo();
  }

  function observeRevealTargets(root, cleanup) {
    const targets = root.querySelectorAll(
      '[data-brand-legacy], .global-market-section, .brand-signoff'
    );
    if (!targets.length) return;

    if (!('IntersectionObserver' in window)) {
      targets.forEach((target) => target.classList.add('is-visible'));
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      });
    }, { threshold: 0.16 });

    targets.forEach((target) => {
      if (target.matches('[data-brand-legacy]')) {
        target.classList.add('has-story-reveal');
      }
      observer.observe(target);
    });
    cleanup.push(() => observer.disconnect());
  }

  function initAssetPreheater(root, cleanup) {
    const images = Array.from(root.querySelectorAll('img[loading="lazy"]'));
    if (!images.length) return;

    let warmed = false;
    const preloaders = [];
    const selectedSource = (image) => {
      const picture = image.closest('picture');
      if (picture) {
        const source = Array.from(picture.querySelectorAll('source')).find((candidate) => {
          const media = candidate.getAttribute('media');
          return !media || window.matchMedia(media).matches;
        });
        const srcset = source?.getAttribute('srcset');
        if (srcset) return srcset.split(',')[0].trim().split(/\s+/)[0];
      }
      return image.currentSrc || image.src;
    };
    const warm = () => {
      if (warmed) return;
      warmed = true;
      images.forEach((image, index) => {
        image.loading = 'eager';
        image.fetchPriority = index === 0 ? 'auto' : 'low';
        image.dataset.noyafaPrewarmed = 'true';
        image.decode?.().catch(() => undefined);
        const source = selectedSource(image);
        if (!source) return;
        const preloader = new Image();
        preloader.decoding = 'async';
        preloader.fetchPriority = index === 0 ? 'auto' : 'low';
        preloader.src = source;
        preloader.decode?.()
          .then(() => {
            image.dataset.noyafaPreloaded = 'true';
          })
          .catch(() => undefined);
        preloaders.push(preloader);
      });
    };
    root.noyafaWarmAssets = warm;

    if (!('IntersectionObserver' in window)) {
      warm();
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      warm();
      observer.disconnect();
    }, {
      rootMargin: `${Math.max(1400, Math.round(window.innerHeight * 2.1))}px 0px`,
      threshold: 0
    });

    observer.observe(root);
    cleanup.push(() => {
      observer.disconnect();
      preloaders.splice(0);
      delete root.noyafaWarmAssets;
    });
  }

  function scheduleBackgroundAssetWarmup() {
    if (assetWarmupScheduled) return;
    assetWarmupScheduled = true;

    const sections = Array.from(document.querySelectorAll('[data-noyafa-brand-section]'));
    const startTimer = window.setTimeout(() => {
      sections.forEach((section, index) => {
        const timer = window.setTimeout(() => {
          const warm = () => section.noyafaWarmAssets?.();
          if ('requestIdleCallback' in window) {
            window.requestIdleCallback(warm, { timeout: 900 });
          } else {
            warm();
          }
        }, index * 620);
        assetWarmupTimers.push(timer);
      });
    }, 2200);
    assetWarmupTimers.push(startTimer);
  }

  function initCounters(root, cleanup) {
    const counters = Array.from(root.querySelectorAll('.digit-counter'));
    if (!counters.length) return;

    const renderCounter = (element, progress) => {
      const target = Number.parseInt(element.dataset.target || '0', 10);
      const suffix = element.dataset.suffix || '';
      const value = Math.round(target * (1 - Math.pow(1 - progress, 3)));
      element.textContent = `${value.toLocaleString('en-US')}${suffix}`;
    };

    const start = (element) => {
      if (element.dataset.counterStarted === 'true') return;
      element.dataset.counterStarted = 'true';
      if (reducedMotion.matches) {
        renderCounter(element, 1);
        return;
      }

      const startedAt = performance.now();
      let frame = 0;
      const tick = (now) => {
        const progress = Math.min(1, (now - startedAt) / 1800);
        renderCounter(element, progress);
        if (progress < 1) frame = requestAnimationFrame(tick);
      };
      frame = requestAnimationFrame(tick);
      cleanup.push(() => cancelAnimationFrame(frame));
    };

    if (!('IntersectionObserver' in window)) {
      counters.forEach(start);
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        start(entry.target);
        observer.unobserve(entry.target);
      });
    }, { threshold: 0.42 });
    counters.forEach((counter) => observer.observe(counter));
    cleanup.push(() => observer.disconnect());
  }

  function initHorizontalCategoryStage(root, cleanup) {
    const stage = root.querySelector('#horizontal-stage');
    const track = root.querySelector('#horizontal-track');
    if (!stage || !track) return;

    const desktopMode = window.matchMedia('(min-width: 1025px) and (min-height: 620px)');
    let frame = 0;

    const render = () => {
      frame = 0;
      if (!desktopMode.matches) {
        stage.classList.remove('is-scroll-driven');
        track.style.removeProperty('transform');
        return;
      }

      stage.classList.add('is-scroll-driven');
      const stageRect = stage.getBoundingClientRect();
      const stickyOffset = Number.parseFloat(
        getComputedStyle(document.documentElement)
          .getPropertyValue('--noyafa-brand-sticky-offset')
      ) || 0;
      const distance = Math.max(0, track.scrollWidth - stage.clientWidth);
      const travel = Math.max(1, stage.offsetHeight - track.offsetHeight);
      const progress = clamp((stickyOffset - stageRect.top) / travel);
      track.style.transform = `translate3d(${-distance * progress}px, 0, 0)`;
    };

    const schedule = () => {
      if (frame) return;
      frame = requestAnimationFrame(render);
    };

    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule, { passive: true });
    desktopMode.addEventListener?.('change', schedule);
    schedule();

    cleanup.push(() => {
      cancelAnimationFrame(frame);
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      desktopMode.removeEventListener?.('change', schedule);
      stage.classList.remove('is-scroll-driven');
      track.style.removeProperty('transform');
    });
  }

  function initBrandStrip(root, cleanup) {
    const track = root.querySelector('.brand-signoff-track');
    const runs = track ? Array.from(track.querySelectorAll('.brand-signoff-sequence')) : [];
    if (!track || runs.length < 2) return;

    const firstRun = runs[0];
    const secondRun = runs[1];
    const seedMarkup = firstRun.innerHTML;
    let resizeFrame = 0;

    const rebuild = () => {
      resizeFrame = 0;
      track.classList.remove('is-ready');
      firstRun.innerHTML = seedMarkup;
      const targetWidth = Math.max(window.innerWidth * 1.35, 1800);
      let guard = 0;
      while (firstRun.scrollWidth < targetWidth && guard < 16) {
        firstRun.insertAdjacentHTML('beforeend', seedMarkup);
        guard += 1;
      }
      secondRun.innerHTML = firstRun.innerHTML;
      track.querySelectorAll('img').forEach((image) => {
        image.loading = 'eager';
        image.fetchPriority = 'low';
        image.decode?.().catch(() => undefined);
      });
      const distance = firstRun.getBoundingClientRect().width;
      track.style.setProperty('--brand-strip-distance', `${distance}px`);
      requestAnimationFrame(() => track.classList.add('is-ready'));
    };

    const schedule = () => {
      if (resizeFrame) cancelAnimationFrame(resizeFrame);
      resizeFrame = requestAnimationFrame(rebuild);
    };

    rebuild();
    window.addEventListener('resize', schedule, { passive: true });
    cleanup.push(() => {
      cancelAnimationFrame(resizeFrame);
      window.removeEventListener('resize', schedule);
      track.classList.remove('is-ready');
      track.style.removeProperty('--brand-strip-distance');
      firstRun.innerHTML = seedMarkup;
      secondRun.innerHTML = seedMarkup;
    });
  }

  function initGsapInteractions(root, cleanup) {
    const slogan = root.querySelector('[data-slogan-reveal]');
    const stickyTexts = Array.from(root.querySelectorAll('.scroll-trigger-text'));
    const stickyImages = Array.from(root.querySelectorAll('.sticky-img'));
    const stickyPanels = Array.from(root.querySelectorAll('.strength-visual'));

    if (!slogan && !stickyTexts.length) return;

    ensureGsap().then((available) => {
      if (!available || !root.isConnected) {
        return;
      }

      const gsap = window.gsap;
      const ScrollTrigger = window.ScrollTrigger;

      if (slogan) {
        slogan.style.setProperty('--slogan-reveal', '0%');
        const tween = gsap.to(slogan, {
          '--slogan-reveal': '100%',
          ease: 'none',
          scrollTrigger: {
            trigger: slogan,
            start: 'top 88%',
            end: 'bottom 48%',
            scrub: reducedMotion.matches ? false : 1
          }
        });
        cleanup.push(() => {
          tween.scrollTrigger?.kill();
          tween.kill();
        });
      }

      if (stickyTexts.length) {
        const activate = (index) => {
          stickyImages.forEach((image, imageIndex) => {
            image.classList.toggle('active', imageIndex === index);
          });
          stickyPanels.forEach((panel, panelIndex) => {
            panel.classList.toggle('active', panelIndex === index);
          });
        };

        stickyTexts.forEach((text, index) => {
          const trigger = ScrollTrigger.create({
            trigger: text,
            start: 'top center',
            end: 'bottom center',
            onEnter: () => activate(index),
            onEnterBack: () => activate(index)
          });
          cleanup.push(() => trigger.kill());
        });
      }

      requestAnimationFrame(() => ScrollTrigger.refresh());
    });
  }

  function syncBrandModalState() {
    const active = Boolean(document.querySelector('.video-modal.is-active, .inquiry-modal.is-open'));
    document.body.classList.toggle('noyafa-brand-modal-open', active);
  }

  function openVideo(trigger) {
    const modal = document.getElementById('video-modal');
    const frame = document.getElementById('video-modal-frame');
    const fallbackLink = document.getElementById('video-modal-fallback-link');
    const closeButton = modal?.querySelector('.video-modal-close');
    const videoId = trigger?.dataset.videoId;
    if (!modal || !frame || !videoId) return;

    videoReturnFocus = document.activeElement;
    savedRootOverflow = document.documentElement.style.overflow;
    frame.src = `https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1&rel=0&modestbranding=1&playsinline=1`;
    if (fallbackLink) fallbackLink.href = `https://www.youtube.com/watch?v=${videoId}`;
    modal.classList.add('is-active');
    modal.setAttribute('aria-hidden', 'false');
    document.documentElement.style.overflow = 'hidden';
    syncBrandModalState();
    closeButton?.focus({ preventScroll: true });
  }

  function closeVideo() {
    const modal = document.getElementById('video-modal');
    const frame = document.getElementById('video-modal-frame');
    if (!modal || !frame) return;
    modal.classList.remove('is-active');
    modal.setAttribute('aria-hidden', 'true');
    frame.src = '';
    document.documentElement.style.overflow = savedRootOverflow;
    syncBrandModalState();
    videoReturnFocus?.focus?.({ preventScroll: true });
    videoReturnFocus = null;
  }

  function openInquiry() {
    const modal = document.getElementById('inquiry-modal');
    if (!modal) return;
    inquiryReturnFocus = document.activeElement;
    modal.classList.add('is-open');
    modal.setAttribute('aria-hidden', 'false');
    syncBrandModalState();
    const firstInput = modal.querySelector('input:not([type="hidden"]), select, textarea');
    window.setTimeout(() => firstInput?.focus({ preventScroll: true }), 120);
  }

  function closeInquiry() {
    const modal = document.getElementById('inquiry-modal');
    if (!modal) return;
    modal.classList.remove('is-open');
    modal.setAttribute('aria-hidden', 'true');
    syncBrandModalState();
    inquiryReturnFocus?.focus?.({ preventScroll: true });
    inquiryReturnFocus = null;
  }

  function readInquiryDraft() {
    try {
      const draft = JSON.parse(window.sessionStorage.getItem(inquiryDraftKey) || 'null');
      if (!draft || !draft.values || Date.now() - draft.updatedAt > inquiryDraftLifetime) {
        window.sessionStorage.removeItem(inquiryDraftKey);
        return null;
      }
      return draft;
    } catch (error) {
      return null;
    }
  }

  function clearInquiryDraft() {
    try {
      window.sessionStorage.removeItem(inquiryDraftKey);
    } catch (error) {
      // Storage can be unavailable in privacy-restricted browsing modes.
    }
  }

  function saveInquiryDraft(form, pending = false) {
    const values = {};
    form.querySelectorAll('input[name^="contact["], select[name^="contact["], textarea[name^="contact["]').forEach((field) => {
      if (field.type === 'hidden' || field.disabled) return;
      values[field.name] = field.value;
    });

    const hasContent = Object.values(values).some((value) => String(value).trim());
    if (!hasContent) {
      clearInquiryDraft();
      return;
    }

    try {
      window.sessionStorage.setItem(inquiryDraftKey, JSON.stringify({
        values,
        pending,
        updatedAt: Date.now()
      }));
    } catch (error) {
      // The form remains fully usable when storage is unavailable.
    }
  }

  function restoreInquiryDraft(form, draft) {
    if (!draft?.values) return;
    Object.entries(draft.values).forEach(([name, value]) => {
      const field = Array.from(form.elements).find((element) => element.name === name);
      if (field && !field.disabled) field.value = value;
    });
  }

  function initInquiryDraft(root, cleanup) {
    const form = root.querySelector('#NoyafaBrandInquiry');
    if (!form) return;

    const success = form.querySelector('[data-inquiry-success]');
    const error = form.querySelector('[data-inquiry-error]');
    if (success) {
      clearInquiryDraft();
      requestAnimationFrame(openInquiry);
      return;
    }

    const draft = readInquiryDraft();
    if (draft) restoreInquiryDraft(form, draft);
    if (draft || error) requestAnimationFrame(openInquiry);

    let saveTimer = 0;
    const scheduleSave = () => {
      window.clearTimeout(saveTimer);
      saveTimer = window.setTimeout(() => saveInquiryDraft(form), 180);
    };
    const saveBeforeChallenge = () => {
      window.clearTimeout(saveTimer);
      saveInquiryDraft(form, true);
    };

    form.addEventListener('input', scheduleSave);
    form.addEventListener('change', scheduleSave);
    form.addEventListener('submit', saveBeforeChallenge, true);
    cleanup.push(() => {
      window.clearTimeout(saveTimer);
      form.removeEventListener('input', scheduleSave);
      form.removeEventListener('change', scheduleSave);
      form.removeEventListener('submit', saveBeforeChallenge, true);
    });
  }

  function bindSharedEvents() {
    if (sharedEventsBound) return;
    sharedEventsBound = true;

    document.addEventListener('click', (event) => {
      const videoTrigger = event.target.closest('[data-video-id]');
      if (videoTrigger) {
        event.preventDefault();
        openVideo(videoTrigger);
        return;
      }
      if (event.target.closest('[data-video-close]')) {
        event.preventDefault();
        closeVideo();
        return;
      }
      if (event.target.closest('[data-inquiry-open]')) {
        event.preventDefault();
        openInquiry();
        return;
      }
      if (event.target.closest('[data-inquiry-close]')) {
        event.preventDefault();
        closeInquiry();
      }
    });

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      closeVideo();
      closeInquiry();
    });

    document.addEventListener('wheel', (event) => {
      if (!document.body.classList.contains('noyafa-brand-modal-open')) return;
      if (event.target.closest('.inquiry-modal__panel, .video-modal-frame-wrap')) return;
      event.preventDefault();
    }, { passive: false });

    document.addEventListener('touchmove', (event) => {
      if (!document.body.classList.contains('noyafa-brand-modal-open')) return;
      if (event.target.closest('.inquiry-modal__panel, .video-modal-frame-wrap')) return;
      event.preventDefault();
    }, { passive: false });

    window.addEventListener('resize', scheduleStoreChromeSync, { passive: true });
    window.addEventListener('orientationchange', scheduleStoreChromeSync);

    if ('MutationObserver' in window) {
      promoObserver = new MutationObserver(dismissStorePromo);
      promoObserver.observe(document.body, { childList: true, subtree: true });
    }

    document.addEventListener('shopify:section:load', (event) => {
      const section = event.target.querySelector?.('[data-noyafa-brand-section]');
      if (section) initSection(section);
      syncPageState();
      window.ScrollTrigger?.refresh();
    });

    document.addEventListener('shopify:section:unload', (event) => {
      const section = event.target.querySelector?.('[data-noyafa-brand-section]');
      const controller = section ? controllers.get(section) : null;
      controller?.destroy();
      if (section) controllers.delete(section);
      syncPageState();
      window.ScrollTrigger?.refresh();
    });
  }

  function initSection(root) {
    if (!root || controllers.has(root)) return;
    const cleanup = [];
    initAssetPreheater(root, cleanup);
    observeRevealTargets(root, cleanup);
    initCounters(root, cleanup);
    initHorizontalCategoryStage(root, cleanup);
    initBrandStrip(root, cleanup);
    initInquiryDraft(root, cleanup);
    initGsapInteractions(root, cleanup);

    if (root.querySelector('.inquiry-form-status') && !root.querySelector('#NoyafaBrandInquiry')) {
      requestAnimationFrame(openInquiry);
    }

    controllers.set(root, {
      destroy() {
        cleanup.splice(0).reverse().forEach((fn) => fn());
      }
    });
  }

  function initAll() {
    bindSharedEvents();
    document.querySelectorAll('[data-noyafa-brand-section]').forEach(initSection);
    scheduleBackgroundAssetWarmup();
    syncPageState();
  }

  window.NoyafaBrandPage = { initAll, openInquiry, closeInquiry };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll, { once: true });
  } else {
    initAll();
  }
})();
